import { TFunction } from '@getpopsure/i18n-react';
import { toast } from '@popsure/dirty-swan';
import { StripeError } from '@stripe/stripe-js';
import { AxiosError } from 'axios';
import routes from 'constants/routes';
import dayjs from 'dayjs';
import { ValidVerticalUrlParams } from 'features/paymentScreen/utils/verticalId.utils';
import {
  CollectableBillingAddress,
  StripeBillingAddress,
  ZStripeBillingAddress,
} from 'models/paymentMethods';
import { GenericQuestionnaireState } from 'reducers/genericQuestionnaire';
import browserHistory from 'shared/browserHistory';
import { paramsSetUrl } from 'shared/util/paramsSetUrl';

const DATE_FORMAT = 'DD MMM YYYY';
const history = browserHistory;

const isValidStripeBillingAddress = (
  value: Partial<StripeBillingAddress>
): value is StripeBillingAddress => {
  return ZStripeBillingAddress.safeParse(value).success;
};

export const toStripeBillingAddress = (
  collectableAddress: Partial<CollectableBillingAddress>
): StripeBillingAddress | undefined => {
  const stripeBillingAddress: Partial<StripeBillingAddress> = {
    ...collectableAddress,
    country: collectableAddress.country?.code,
  };

  if (isValidStripeBillingAddress(stripeBillingAddress)) {
    return stripeBillingAddress;
  }

  return undefined;
};

export const isStripeError = (error: unknown): error is StripeError => {
  return (
    error !== null &&
    typeof error === 'object' &&
    ['type', 'charge', 'payment_intent', 'payment_method', 'setup_intent'].some(
      (property: string) =>
        Object.prototype.hasOwnProperty.call(error, property)
    )
  );
};

export const showNetworkError = (error: AxiosError, t: TFunction) => {
  let message = t(
    'paymentScreen.errors.genericError',
    'We could not process your payment because of an error on our side, please contact support.'
  );

  if (error.response?.status === 402) {
    if (
      typeof error.response.data === 'object' &&
      error.response.data !== null
    ) {
      const data = error.response.data as Record<string, unknown>;
      if ('message' in data && typeof data.message === 'string') {
        message = data.message;
      }
    }
  }

  toast(t('paymentScreen.errors.title', 'Payment error'), {
    duration: 1000000000,
    description: message,
    type: 'error',
  });
};

export const showStripeError = (_error: StripeError, t: TFunction) => {
  toast(t('paymentScreen.errors.title', 'Payment error'), {
    duration: 1000000000,
    description: t(
      'paymentScreen.errors.genericStripeError',
      'There was an issue with your payment method, please review your payment details or contact support if you need help.'
    ),
    type: 'error',
  });
};

export const formatProratedPeriod = (period: {
  start: string;
  end: string;
}): string => {
  const startDate = dayjs(period?.start).format(DATE_FORMAT);
  const endDate = dayjs(period?.end).format(DATE_FORMAT);

  if (startDate === endDate) {
    return `${startDate}`;
  }

  return `${startDate} - ${endDate}`;
};

export const redirectSuccessfulCheckout = (
  verticalStateId?: keyof GenericQuestionnaireState,
  mainPolicyId?: string,
  region?: string
) => {
  window.scrollTo(0, 0);
  history.push(
    paramsSetUrl<
      'signupSuccess' | 'mainPolicyId' | 'region',
      ValidVerticalUrlParams | string
    >(routes.me.policies.path, [
      { key: 'signupSuccess', value: verticalStateId ?? '' },
      { key: 'mainPolicyId', value: mainPolicyId ?? '' },
      { key: 'region', value: region ?? '' },
    ])
  );
};
